import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { formatPlate } from './utils/plateFormat';

const API = '/api';

function getStored() {
  try {
    const t = localStorage.getItem('nasati_token');
    const u = localStorage.getItem('nasati_user');
    if (t && u) return { token: t, user: JSON.parse(u) };
  } catch (e) {}
  return null;
}

function apiFetch(path, options = {}) {
  const auth = getStored();
  const headers = { 'Content-Type': 'application/json', ...options.headers };
  if (auth) headers['Authorization'] = 'Bearer ' + auth.token;
  return fetch(API + path, { ...options, headers });
}

const FAMILIES_CARD_OPEN_KEY = 'nasati_web_familiesCardOpen';
function getStoredFamiliesCardOpen() {
  try {
    return localStorage.getItem(FAMILIES_CARD_OPEN_KEY) === 'true';
  } catch (_) {
    return false;
  }
}

const formatDate = (s) => {
  if (!s) return '—';
  return new Date(s).toLocaleString('he-IL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const styles = {
  page: {
    minHeight: '100vh',
    background: '#f0fdfa',
    fontFamily: 'system-ui, sans-serif',
    direction: 'rtl',
    textAlign: 'right',
    padding: 24,
  },
  container: { maxWidth: 900, margin: '0 auto' },
  section: { marginBottom: 24, textAlign: 'right' },
  sectionTitle: { color: '#0f766e', fontSize: 18, marginBottom: 12, textAlign: 'right' },
  totalsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 16,
    marginBottom: 24,
    direction: 'rtl',
  },
  totalCard: {
    background: '#fff',
    padding: '16px 24px',
    borderRadius: 12,
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    minWidth: 120,
    textAlign: 'center',
  },
  totalValue: { fontSize: 28, fontWeight: 700, color: '#0f766e' },
  totalLabel: { fontSize: 14, color: '#64748b', marginTop: 4 },
  card: {
    background: '#fff',
    borderRadius: 12,
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    marginBottom: 12,
    overflow: 'hidden',
    textAlign: 'right',
  },
  cardHeader: {
    padding: '14px 16px',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 8,
    cursor: 'pointer',
    direction: 'rtl',
  },
  familyCardHeader: {
    padding: '14px 16px',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 8,
    cursor: 'pointer',
    direction: 'rtl',
    background: '#ccfbf1',
    borderRadius: 8,
  },
  summaryCardHeader: {
    padding: '14px 16px',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 8,
    cursor: 'pointer',
    direction: 'rtl',
    background: '#f8fafc',
    borderRadius: 12,
  },
  cardTitle: { fontWeight: 600, color: '#334155', fontSize: 16 },
  cardMeta: { fontSize: 14, color: '#64748b' },
  bidiLtr: { direction: 'ltr', unicodeBidi: 'embed', display: 'inline' },
  bidiIsolate: { unicodeBidi: 'isolate' },
  cardBody: { padding: '0 16px 16px', borderTop: '1px solid #e2e8f0', textAlign: 'right' },
  nestedCardBody: { padding: '12px 16px 16px', paddingRight: 24, textAlign: 'right', background: '#f8fafc' },
  userItemRow: {
    padding: '10px 12px',
    marginBottom: 8,
    background: '#fff',
    borderRadius: 8,
    border: '1px solid #e2e8f0',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 8,
    direction: 'rtl',
  },
  button: {
    background: '#0f766e',
    color: '#fff',
    border: 'none',
    padding: '8px 14px',
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    marginLeft: 8,
  },
  buttonDanger: { background: '#dc2626' },
  buttonSecondary: { background: '#64748b' },
  errorText: { color: '#dc2626', fontSize: 14, marginTop: 8 },
  tripRow: { padding: '8px 0', borderBottom: '1px solid #e2e8f0', fontSize: 14, color: '#475569' },
  tripListScroll: { maxHeight: 320, overflowY: 'auto' },
  select: {
    padding: 10,
    borderRadius: 8,
    border: '1px solid #e2e8f0',
    fontSize: 14,
    textAlign: 'right',
    direction: 'rtl',
    minWidth: 200,
  },
  assignRow: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, marginBottom: 16, direction: 'rtl' },
};

export default function AdminPage() {
  const [stats, setStats] = useState(null);
  const [families, setFamilies] = useState([]);
  const [users, setUsers] = useState([]);
  const [allVehicles, setAllVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedFamiliesSummary, setExpandedFamiliesSummary] = useState(getStoredFamiliesCardOpen);
  const [expandedFamilyId, setExpandedFamilyId] = useState(null);
  const [familyTrips, setFamilyTrips] = useState({});
  const [assignUserId, setAssignUserId] = useState('');
  const [setAdminLoading, setSetAdminLoading] = useState(false);

  const loadStats = useCallback(async () => {
    try {
      const res = await apiFetch('/admin/stats');
      if (!res.ok) throw new Error('לא מורשה');
      const data = await res.json();
      setStats(data);
      setFamilies(data.families || []);
      setUsers(data.users || []);
      setError('');
    } catch (e) {
      setError(e.message || 'שגיאה בטעינה');
      setStats(null);
      setFamilies([]);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadVehicles = useCallback(async () => {
    try {
      const res = await apiFetch('/admin/vehicles');
      if (res.ok) {
        const data = await res.json();
        setAllVehicles(Array.isArray(data) ? data : []);
      }
    } catch (_) {}
  }, []);

  useEffect(() => {
    loadStats();
    loadVehicles();
  }, [loadStats, loadVehicles]);

  const loadFamilyTrips = useCallback(async (familyId) => {
    if (familyTrips[familyId]) return;
    try {
      const res = await apiFetch(`/admin/trips?familyId=${familyId}`);
      const data = await res.json();
      setFamilyTrips((prev) => ({ ...prev, [familyId]: Array.isArray(data) ? data : [] }));
    } catch (_) {
      setFamilyTrips((prev) => ({ ...prev, [familyId]: [] }));
    }
  }, [familyTrips]);

  const toggleFamiliesSummary = () => {
    setExpandedFamiliesSummary((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(FAMILIES_CARD_OPEN_KEY, next ? 'true' : 'false');
      } catch (_) {}
      return next;
    });
  };

  const toggleFamily = (id, e) => {
    if (e) e.stopPropagation();
    if (expandedFamilyId === id) {
      setExpandedFamilyId(null);
      return;
    }
    setExpandedFamilyId(id);
    loadFamilyTrips(id);
  };

  const handleSetAdmin = async (userId, admin) => {
    setSetAdminLoading(true);
    try {
      const res = await apiFetch(`/admin/users/${userId}/set-admin`, {
        method: 'PATCH',
        body: JSON.stringify({ admin: admin ? 1 : 0 }),
      });
      if (!res.ok) throw new Error('שגיאה');
      await loadStats();
      setAssignUserId('');
    } catch (e) {
      setError(e.message);
    } finally {
      setSetAdminLoading(false);
    }
  };

  const handleDeleteFamily = async (id, name) => {
    if (!window.confirm(`למחוק את המשפחה "${name || id}" וכל הנתונים המשויכים?`)) return;
    try {
      const res = await apiFetch(`/admin/families/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('שגיאה במחיקה');
      await loadStats();
    } catch (e) {
      setError(e.message);
    }
  };

  const handleDeleteUser = async (id, name) => {
    if (!window.confirm(`למחוק את המשתמש "${name || id}"?`)) return;
    try {
      const res = await apiFetch(`/admin/users/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('שגיאה במחיקה');
      await loadStats();
    } catch (e) {
      setError(e.message);
    }
  };

  const handleDeleteVehicle = async (id, name, plate) => {
    if (!window.confirm(`למחוק את הרכב "${(name || '').trim()} ${formatPlate(plate || '') || id}"?`)) return;
    try {
      const res = await apiFetch(`/admin/vehicles/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('שגיאה במחיקה');
      await loadStats();
      await loadVehicles();
    } catch (e) {
      setError(e.message);
    }
  };

  if (loading) {
    return (
      <div className="nasati-page" style={styles.page} lang="he" dir="rtl">
        <div className="nasati-admin-container" style={styles.container}><p style={styles.sectionTitle}>טוען...</p></div>
      </div>
    );
  }

  if (error && !stats) {
    return (
      <div className="nasati-page" style={styles.page} lang="he" dir="rtl">
        <div className="nasati-admin-container" style={styles.container}>
          <p style={styles.errorText}>{error}</p>
          <Link to="/">חזרה לדוחות</Link>
        </div>
      </div>
    );
  }

  const totals = stats?.totals || {};
  const familiesList = stats?.families || families;
  const usersList = stats?.users || users;

  return (
    <div className="nasati-page" style={styles.page} lang="he" dir="rtl">
      <div className="nasati-admin-container" style={styles.container}>
        <section className="nasati-section" style={styles.section}>
          <h2 className="nasati-section-title" style={styles.sectionTitle}>סיכום</h2>
          <div className="nasati-totals-row" style={styles.totalsRow}>
            <div className="nasati-total-card" style={styles.totalCard}>
              <div className="nasati-total-value" style={styles.totalValue}>{totals.families ?? 0}</div>
              <div style={styles.totalLabel}>משפחות</div>
            </div>
            <div className="nasati-total-card" style={styles.totalCard}>
              <div className="nasati-total-value" style={styles.totalValue}>{totals.users ?? 0}</div>
              <div style={styles.totalLabel}>משתמשים</div>
            </div>
            <div className="nasati-total-card" style={styles.totalCard}>
              <div className="nasati-total-value" style={styles.totalValue}>{totals.vehicles ?? 0}</div>
              <div style={styles.totalLabel}>רכבים</div>
            </div>
            <div className="nasati-total-card" style={styles.totalCard}>
              <div className="nasati-total-value" style={styles.totalValue}>{totals.trips ?? 0}</div>
              <div style={styles.totalLabel}>נסיעות</div>
            </div>
          </div>
        </section>

        <section className="nasati-section" style={styles.section}>
          <h2 className="nasati-section-title" style={styles.sectionTitle}>הפוך למנהל / הסר מנהל</h2>
          <div className="nasati-assign-row" style={styles.assignRow}>
            <select
              className="nasati-select"
              style={styles.select}
              value={assignUserId}
              onChange={(e) => setAssignUserId(e.target.value)}
              aria-label="בחר משתמש"
            >
              <option value="">בחר משתמש</option>
              {usersList.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name} {u.email ? `(${u.email})` : ''} {u.admin ? '— מנהל' : ''}
                </option>
              ))}
            </select>
            {assignUserId && (
              <>
                <button
                  type="button"
                  className="nasati-button"
                  style={styles.button}
                  disabled={setAdminLoading}
                  onClick={() => handleSetAdmin(assignUserId, true)}
                >
                  הפוך למנהל
                </button>
                <button
                  type="button"
                  className="nasati-button"
                  style={{ ...styles.button, ...styles.buttonSecondary }}
                  disabled={setAdminLoading}
                  onClick={() => handleSetAdmin(assignUserId, false)}
                >
                  הסר מנהל
                </button>
              </>
            )}
          </div>
        </section>

        <section className="nasati-section" style={styles.section}>
          <div style={styles.card}>
            <div
              className="nasati-card-header"
              style={styles.summaryCardHeader}
              onClick={toggleFamiliesSummary}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && toggleFamiliesSummary()}
              aria-expanded={expandedFamiliesSummary}
            >
              <span style={styles.cardTitle}>משפחות</span>
              <span style={styles.cardMeta}>
                {totals.families ?? 0} משפחות · {totals.users ?? 0} משתמשים
                {expandedFamiliesSummary ? ' ▼' : ' ◀'}
              </span>
            </div>
            {expandedFamiliesSummary && (
              <div style={styles.cardBody}>
                {familiesList.length === 0 && <p style={styles.cardMeta}>אין משפחות.</p>}
                {familiesList.map((f) => {
                  const familyUsers = usersList.filter((u) => u.family_id === f.id);
                  return (
                    <div key={f.id} style={{ ...styles.card, marginBottom: 12, marginTop: 8 }}>
                      <div
                        className="nasati-card-header"
                        style={styles.familyCardHeader}
                        onClick={(e) => toggleFamily(f.id, e)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && toggleFamily(f.id, e)}
                        aria-expanded={expandedFamilyId === f.id}
                      >
                        <div>
                          <span style={styles.cardTitle}>{f.name || f.invite_code || f.id}</span>
                          <span style={styles.cardMeta}>
                            <span dir="rtl" style={styles.bidiIsolate}>— קוד: </span>
                            <span dir="ltr" style={styles.bidiLtr}>{f.invite_code}</span>
                            <span dir="rtl" style={styles.bidiIsolate}> · </span>
                            <span dir="rtl" style={styles.bidiIsolate}><span dir="ltr" style={styles.bidiLtr}>{f.user_count}</span> משתמשים</span>
                            <span dir="rtl" style={styles.bidiIsolate}> · </span>
                            <span dir="rtl" style={styles.bidiIsolate}><span dir="ltr" style={styles.bidiLtr}>{f.vehicle_count}</span> רכבים</span>
                            <span dir="rtl" style={styles.bidiIsolate}> · </span>
                            <span dir="rtl" style={styles.bidiIsolate}><span dir="ltr" style={styles.bidiLtr}>{f.trip_count}</span> נסיעות</span>
                            {f.creator_name ? <span dir="rtl" style={styles.bidiIsolate}> · נוצר ע"י {f.creator_name}</span> : ''}
                            {expandedFamilyId === f.id ? ' ▼' : ' ◀'}
                          </span>
                        </div>
                        <button
                          type="button"
                          className="nasati-button"
                          style={{ ...styles.button, ...styles.buttonDanger }}
                          onClick={(ev) => { ev.stopPropagation(); handleDeleteFamily(f.id, f.name); }}
                        >
                          מחק
                        </button>
                      </div>
                      {expandedFamilyId === f.id && (
                        <div style={styles.nestedCardBody}>
                          <div style={{ marginBottom: 12, fontWeight: 600, color: '#0f766e', fontSize: 14 }}>משתמשים במשפחה</div>
                          {familyUsers.length === 0 ? (
                            <p style={styles.cardMeta}>אין משתמשים.</p>
                          ) : (
                            familyUsers.map((u) => (
                              <div key={u.id} style={styles.userItemRow}>
                                <div>
                                  <span style={styles.cardTitle}>{u.name} {u.admin ? '(מנהל)' : ''}</span>
                                  <span style={styles.cardMeta}> — {u.email || 'ללא אימייל'} · <span dir="ltr" style={styles.bidiLtr}>{u.trip_count}</span> נסיעות</span>
                                </div>
                                <button
                                  type="button"
                                  className="nasati-button"
                                  style={{ ...styles.button, ...styles.buttonDanger }}
                                  onClick={(ev) => { ev.stopPropagation(); handleDeleteUser(u.id, u.name); }}
                                >
                                  מחק
                                </button>
                              </div>
                            ))
                          )}
                          {(() => {
                            const familyVehicles = allVehicles.filter((v) => String(v.family_id) === String(f.id));
                            const byType = {
                              car: familyVehicles.filter((v) => (v.vehicle_type || v.type || 'car') !== 'motorbike'),
                              motorbike: familyVehicles.filter((v) => (v.vehicle_type || v.type) === 'motorbike'),
                            };
                            const typeLabels = { car: 'מכוניות', motorbike: 'אופנועים' };
                            if (familyVehicles.length === 0) {
                              return <><div style={{ marginTop: 16, marginBottom: 12, fontWeight: 600, color: '#0f766e', fontSize: 14 }}>רכבים</div><p style={styles.cardMeta}>אין רכבים.</p></>;
                            }
                            return (
                              <>
                                {(['car', 'motorbike']).map((type) => {
                                  const list = byType[type];
                                  if (list.length === 0) return null;
                                  return (
                                    <div key={type} style={{ marginTop: 16 }}>
                                      <div style={{ marginBottom: 8, fontWeight: 600, color: '#0f766e', fontSize: 14 }}>{typeLabels[type]}</div>
                                      {list.map((v) => (
                                        <div key={v.id} style={styles.userItemRow}>
                                          <span style={styles.cardTitle}>{v.name} — {formatPlate(v.plate)}</span>
                                          <button
                                            type="button"
                                            className="nasati-button"
                                            style={{ ...styles.button, ...styles.buttonDanger }}
                                            onClick={(ev) => { ev.stopPropagation(); handleDeleteVehicle(v.id, v.name, v.plate); }}
                                          >
                                            מחק
                                          </button>
                                        </div>
                                      ))}
                                    </div>
                                  );
                                })}
                              </>
                            );
                          })()}
                          <div style={{ marginTop: 16, fontWeight: 600, color: '#0f766e', fontSize: 14 }}>נסיעות (10 אחרונות)</div>
                          {(familyTrips[f.id] || []).length === 0 && !familyTrips[f.id] ? (
                            <p style={styles.cardMeta}>טוען...</p>
                          ) : (familyTrips[f.id] || []).length === 0 ? (
                            <p style={styles.cardMeta}>אין נסיעות</p>
                          ) : (
                            <div style={styles.tripListScroll}>
                              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                {(familyTrips[f.id] || []).slice(0, 10).map((t) => (
                                  <li key={t.id} style={styles.tripRow}>
                                    {t.vehicle_name} {formatPlate(t.vehicle_plate || '')} — {t.driver_name}: {formatDate(t.start_time)} – {formatDate(t.end_time)}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        {error && <p style={styles.errorText}>{error}</p>}
      </div>
    </div>
  );
}
